/**
 * @license
 * Copyright (c) 2004 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export class NativeDateTimeConstants {
  public static readonly _YEAR_AND_DATE_REGEXP = /(\d{1,4})\D+?(\d{1,4})/g;
  public static readonly _YMD_REGEXP = /(\d{1,4})\D+?(\d{1,4})\D+?(\d{1,4})/g;
  public static readonly _TIME_REGEXP =
    /(\d{1,2})(?:\D(\d{1,2}))?(?:\D(\d{1,2}))?(?:\D(\d{1,3}))?/g;
  public static readonly _TIME_FORMAT_REGEXP = /h|H|K|k/g;

  public static readonly _YEAR_REGEXP = /y{1,4}/;
  public static readonly _MONTH_REGEXP = /M{1,5}/;
  public static readonly _DAY_REGEXP = /d{1,2}/;
  public static readonly _WEEK_DAY_REGEXP = /E{1,5}/;
  public static readonly _HOUR_REGEXP = /h{1,2}|k{1,2}/i;
  public static readonly _MINUTE_REGEXP = /m{1,2}/;
  public static readonly _SECOND_REGEXP = /s{1,2}/;
  public static readonly _FRACTIONAL_SECOND_REGEXP = /S{1,3}/;
  public static readonly _AMPM_REGEXP = /a{1,2}/;
  public static readonly _WORD_REGEXP = '(\\D+?\\s*)';

  // This eslint disable require to make eacs happy
  // eslint-disable-next-line no-useless-escape
  public static readonly _ESCAPE_REGEXP = /([\^$.*+?|\[\](){}])/g;

  public static readonly _TOKEN_REGEXP =
    /ccccc|cccc|ccc|cc|c|EEEEE|EEEE|EEE|EE|E|dd|d|MMMMM|MMMM|MMM|MM|M|LLLLL|LLLL|LLL|LL|L|yyyy|yy|y|hh|h|HH|H|KK|K|kk|k|mm|m|ss|s|aa|a|SSS|SS|S|zzzz|zzz|zz|z|v|ZZZ|ZZ|Z|XXX|XX|X|VV|GGGGG|GGGG|GGG|GG|G/g;
  public static readonly _ZULU = 'zulu';
  public static readonly _LOCAL = 'local';
  public static readonly _AUTO = 'auto';
  public static readonly _INVARIANT = 'invariant';
  public static readonly _OFFSET = 'offset';
  public static readonly _ALNUM_REGEXP = '(\\D+|\\d\\d?\\D|\\d\\d?|\\D+\\d\\d?)';
  public static readonly _NON_DIGIT_REGEXP = '(\\D+|\\D+\\d\\d?)';
  public static readonly _NON_DIGIT_OPT_REGEXP = '(\\D*)';
  public static readonly _STR_REGEXP = '(.+?)';
  public static readonly _TWO_DIGITS_REGEXP = '(\\d\\d?)';
  public static readonly _THREE_DIGITS_REGEXP = '(\\d{1,3})';
  public static readonly _FOUR_DIGITS_REGEXP = '(\\d{1,4})';
  public static readonly _SLASH_REGEXP = '(\\/)';

  public static readonly _PROPERTIES_MAP = {
    MMM: {
      token: 'months',
      style: 'format',
      mLen: 'abbreviated',
      matchIndex: 0,
      key: 'month',
      value: 'short',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    MMMM: {
      token: 'months',
      style: 'format',
      mLen: 'wide',
      matchIndex: 0,
      key: 'month',
      value: 'long',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    MMMMM: {
      token: 'months',
      style: 'format',
      mLen: 'narrow',
      matchIndex: 0,
      key: 'month',
      value: 'narrow',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    LLL: {
      token: 'months',
      style: 'stand-alone',
      mLen: 'abbreviated',
      matchIndex: 1,
      key: 'month',
      value: 'short',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    LLLL: {
      token: 'months',
      style: 'stand-alone',
      mLen: 'wide',
      matchIndex: 1,
      key: 'month',
      value: 'long',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    LLLLL: {
      token: 'months',
      style: 'stand-alone',
      mLen: 'narrow',
      matchIndex: 1,
      key: 'month',
      value: 'narrow',
      regExp: NativeDateTimeConstants._ALNUM_REGEXP
    },
    E: {
      token: 'days',
      style: 'format',
      dLen: 'abbreviated',
      matchIndex: 0,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    EE: {
      token: 'days',
      style: 'format',
      dLen: 'abbreviated',
      matchIndex: 0,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    EEE: {
      token: 'days',
      style: 'format',
      dLen: 'abbreviated',
      matchIndex: 0,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    EEEE: {
      token: 'days',
      style: 'format',
      dLen: 'wide',
      matchIndex: 0,
      key: 'weekday',
      value: 'long',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    EEEEE: {
      token: 'days',
      style: 'format',
      dLen: 'narrow',
      matchIndex: 0,
      key: 'weekday',
      value: 'narrow',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    c: {
      token: 'days',
      style: 'stand-alone',
      dLen: 'abbreviated',
      matchIndex: 1,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    cc: {
      token: 'days',
      style: 'stand-alone',
      dLen: 'abbreviated',
      matchIndex: 1,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    ccc: {
      token: 'days',
      style: 'stand-alone',
      dLen: 'abbreviated',
      matchIndex: 1,
      key: 'weekday',
      value: 'short',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    cccc: {
      token: 'days',
      style: 'stand-alone',
      dLen: 'wide',
      matchIndex: 1,
      key: 'weekday',
      value: 'long',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    ccccc: {
      token: 'days',
      style: 'stand-alone',
      dLen: 'narrow',
      matchIndex: 1,
      key: 'weekday',
      value: 'narrow',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    h: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 11,
      start2: 1,
      end2: 12,
      key: 'hour',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    hh: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 11,
      start2: 1,
      end2: 12,
      key: 'hour',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    K: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 12,
      start2: 0,
      end2: 12,
      key: 'hour',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    KK: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 12,
      start2: 0,
      end2: 12,
      key: 'hour',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    H: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 23,
      start2: 0,
      end2: 23,
      key: 'hour',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    HH: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 23,
      start2: 0,
      end2: 23,
      key: 'hour',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    k: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 24,
      start2: 0,
      end2: 24,
      key: 'hour',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    kk: {
      token: 'time',
      timePart: 'hour',
      start1: 0,
      end1: 24,
      start2: 0,
      end2: 24,
      key: 'hour',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    m: {
      token: 'time',
      timePart: 'minute',
      start1: 0,
      end1: 59,
      start2: 0,
      end2: 59,
      key: 'minute',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    mm: {
      token: 'time',
      timePart: 'minute',
      start1: 0,
      end1: 59,
      start2: 0,
      end2: 59,
      key: 'minute',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    s: {
      token: 'time',
      timePart: 'second',
      start1: 0,
      end1: 59,
      start2: 0,
      end2: 59,
      key: 'second',
      value: 'numeric',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    ss: {
      token: 'time',
      timePart: 'second',
      start1: 0,
      end1: 59,
      start2: 0,
      end2: 59,
      key: 'second',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    S: {
      token: 'time',
      timePart: 'millisec',
      start1: 0,
      end1: 999,
      start2: 0,
      end2: 999,
      key: 'millisecond',
      value: 'numeric',
      regExp: NativeDateTimeConstants._THREE_DIGITS_REGEXP
    },
    SS: {
      token: 'time',
      timePart: 'millisec',
      start1: 0,
      end1: 999,
      start2: 0,
      end2: 999,
      key: 'millisecond',
      value: 'numeric',
      regExp: NativeDateTimeConstants._THREE_DIGITS_REGEXP
    },
    SSS: {
      token: 'time',
      timePart: 'millisec',
      start1: 0,
      end1: 999,
      start2: 0,
      end2: 999,
      key: 'millisecond',
      value: 'numeric',
      regExp: NativeDateTimeConstants._THREE_DIGITS_REGEXP
    },
    d: {
      token: 'dayOfMonth',
      key: 'day',
      value: 'numeric',
      getPartIdx: 2,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    dd: {
      token: 'dayOfMonth',
      key: 'day',
      value: '2-digit',
      getPartIdx: 2,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    M: {
      token: 'monthIndex',
      key: 'month',
      value: 'numeric',
      getPartIdx: 1,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    MM: {
      token: 'monthIndex',
      key: 'month',
      value: '2-digit',
      getPartIdx: 1,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    L: {
      token: 'monthIndex',
      key: 'month',
      value: 'numeric',
      getPartIdx: 1,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    LL: {
      token: 'monthIndex',
      key: 'month',
      value: '2-digit',
      getPartIdx: 1,
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    y: {
      token: 'year',
      key: 'year',
      value: 'numeric',
      regExp: NativeDateTimeConstants._FOUR_DIGITS_REGEXP
    },
    yy: {
      token: 'year',
      key: 'year',
      value: '2-digit',
      regExp: NativeDateTimeConstants._TWO_DIGITS_REGEXP
    },
    yyyy: {
      token: 'year',
      key: 'year',
      value: 'numeric',
      regExp: NativeDateTimeConstants._FOUR_DIGITS_REGEXP
    },
    a: {
      token: 'ampm',
      key: 'dayPeriod',
      value: undefined,
      regExp: NativeDateTimeConstants._WORD_REGEXP
    },
    z: {
      token: 'tzAbbrev',
      key: 'timeZoneName',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP
    },
    v: {
      token: 'tzAbbrev',
      key: 'timeZoneName',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP
    },
    zz: {
      token: 'tzAbbrev',
      key: 'timeZoneName',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP
    },
    zzz: {
      token: 'tzAbbrev',
      key: 'timeZoneName',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP
    },
    zzzz: {
      token: 'tzFull',
      key: 'timeZoneName',
      value: 'long',
      regExp: NativeDateTimeConstants._STR_REGEXP
    },
    Z: {
      token: 'tzhm',
      key: 'tzhm',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    ZZ: {
      token: 'tzhm',
      key: 'tzhm',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    ZZZ: {
      token: 'tzhm',
      key: 'tzhm',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    X: {
      token: 'tzh',
      key: 'tzh',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    XX: {
      token: 'tzhm',
      key: 'tzhm',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    XXX: {
      token: 'tzhsepm',
      key: 'tzhsepm',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    VV: {
      token: 'tzid',
      key: 'tzid',
      value: 'short',
      regExp: NativeDateTimeConstants._STR_REGEXP,
      type: 'tzOffset'
    },
    G: {
      token: 'era',
      key: 'era',
      value: 'eraAbbr',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    GG: {
      token: 'era',
      key: 'era',
      value: 'eraAbbr',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    GGG: {
      token: 'era',
      key: 'era',
      value: 'eraAbbr',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    GGGG: {
      token: 'era',
      key: 'era',
      value: 'eraName',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    GGGGG: {
      token: 'era',
      key: 'era',
      value: 'eraNarrow',
      regExp: NativeDateTimeConstants._NON_DIGIT_REGEXP
    },
    '/': {
      token: 'slash',
      regExp: NativeDateTimeConstants._SLASH_REGEXP
    }
  };

  public static readonly FRACTIONAL_SECOND_MAP = {
    a: {
      key: 'dayPeriod',
      token: 'dayPeriod',
      value: 'narrow'
    },
    SSS: {
      key: 'fractionalSecondDigits',
      token: 'fractionalSecond',
      value: 3
    },
    SS: {
      key: 'fractionalSecondDigits',
      token: 'fractionalSecond',
      value: 2
    },
    S: {
      key: 'fractionalSecondDigits',
      token: 'fractionalSecond',
      value: 1
    }
  };
  // Map between tokens length and options
  public static readonly _tokenMap = {
    era: { short: 'GGG', long: 'GGGG', narrow: 'GGGGG' },
    month: { short: 'MMM', long: 'MMMM', narrow: 'MMMMM', numeric: 'M', '2-digit': 'MM' },
    weekday: { short: 'EEE', long: 'EEEE', narrow: 'EEEEE' },
    year: { numeric: 'y', '2-digit': 'yy' },
    day: { numeric: 'd', '2-digit': 'dd' },
    hour: { numeric: 'h', '2-digit': 'hh' },
    minute: { numeric: 'm', '2-digit': 'mm' },
    second: { numeric: 's', '2-digit': 'ss' },
    fractionalSecond: { 1: 'S', 2: 'SS', 3: 'SSS' },
    timeZoneName: { short: 'z', long: 'zzzz' }
  };
  // Map between tokens and timeStyle and dateStyle
  // Always set year to y, hour to h, minute to m, and second to s
  // We need to get the pattern from the options. It is only needed by parse,
  // prase does not care of the difference between h an hh, y and yy because
  // they are all numeric. so 2021/09/01 is equivalent to 21/9/1 because the
  // pattern is (\d)+/(\d)+/(\(d)+ matches both of inputs
  public static readonly _dateTimeFormats = {
    dateStyle: {
      full: { year: 'y', month_s: 'MM', month_m: 'MMMM', weekday: 'EEEE', day: 'd' },
      long: { year: 'y', month_s: 'MM', month_m: 'MMMM', day: 'd' },
      medium: { year: 'y', month_s: 'MM', month_m: 'MMM', day: 'd' },
      short: { year: 'y', month_s: 'M', month_m: 'MMM', day: 'd' }
    },
    timeStyle: {
      full: { hour: 'h', minute: 'mm', second: 'ss', timeZoneName: 'zzzz' },
      long: { hour: 'h', minute: 'mm', second: 'ss', timeZoneName: 'z' },
      medium: { hour: 'h', minute: 'mm', second: 'ss' },
      short: { hour: 'h', minute: 'mm' }
    }
  };
  // letters a-z and A-Z are reserved for date-time tokens in the pattern
  // they need to be surrounded by single quote if they are not a token.
  // for example at should appear as 'at' in the pattern
  public static readonly _ALPHA_REGEXP = /([a-zA-Z]+)/;
  public static readonly _HOUR12_REGEXP = /h/g;
  public static readonly _hourCycleMap = {
    h12: 'h',
    h23: 'H',
    h11: 'K',
    h24: 'k'
  };
  // zh-TW locales have different symbols for pm depending on the time.
  // The following entries are noon, after noon, and evening
  public static readonly _zh_tw_locales = ['zh-TW', 'zh-Hant', 'zh-Hant-TW'];
  public static readonly _zh_tw_pm_symbols = ['\u4e2d\u5348', '\u4e0b\u5348', '\u665a\u4e0a'];
}
